<template>
  <main class="all-props-page">
    <league-header
      selected-sport="props"
      selected-league="all-props"
      sport="props"
      :leagues="['all-props']"
      view="exchange"
      :props-filters="propsHeaderFilters"
      :active-props-filter="selectedSport"
      @search-for-event="searchForEvent"
      @refresh-data="fetchAllProps"
      @select-props-filter="onSelectSport"
    />
    <div class="container py-2">
      <div class="hero-search mb-2" :class="{ 'is-searching': searchFocused }">
        <font-awesome-icon
          :icon="faMagnifyingGlass"
          class="hero-search__icon"
          aria-hidden="true"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="hero-search__input"
          :placeholder="searchPlaceholder"
          aria-label="Search props"
          @focus="searchFocused = true"
          @blur="searchFocused = false"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="hero-search__clear"
          aria-label="Clear search"
          @click="searchQuery = ''"
        >
          <font-awesome-icon :icon="faXmark" />
        </button>
        <div class="hero-search__controls">
          <!-- "Filter by" dropdown to the left of Popular. Replaces the old
               games-header sort row: sorts (Earliest / A-Z) plus the grouping
               toggles (By Player / By Game) that used to be preset pills, all
               driving the existing view / sortColumn model. -->
          <div ref="filterMenuRef" class="props-filter">
            <button
              type="button"
              class="props-filter__trigger"
              :class="{ 'is-active': anyFilterActive }"
              :aria-expanded="filterMenuOpen"
              aria-haspopup="menu"
              aria-label="Filter by"
              @click="filterMenuOpen = !filterMenuOpen"
            >
              <span class="props-filter__label">Filter by</span>
              <font-awesome-icon
                :icon="faCaretDown"
                class="props-filter__caret"
                :class="{ open: filterMenuOpen }"
                aria-hidden="true"
              />
            </button>
            <div v-if="filterMenuOpen" class="props-filter__menu" role="menu">
              <button
                type="button"
                class="props-filter__item"
                :class="{ 'is-active': sortColumn === SORT_MODES.STARTING }"
                role="menuitemradio"
                :aria-checked="sortColumn === SORT_MODES.STARTING"
                @click="toggleFilter(SORT_MODES.STARTING)"
              >
                <font-awesome-icon
                  :icon="faClock"
                  class="props-filter__item-icon"
                  aria-hidden="true"
                />
                <span class="props-filter__item-label">Earliest</span>
                <font-awesome-icon
                  v-if="sortColumn === SORT_MODES.STARTING"
                  :icon="faCheck"
                  class="props-filter__check"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                class="props-filter__item"
                :class="{ 'is-active': sortColumn === SORT_MODES.PLAYER }"
                role="menuitemradio"
                :aria-checked="sortColumn === SORT_MODES.PLAYER"
                @click="toggleFilter(SORT_MODES.PLAYER)"
              >
                <font-awesome-icon
                  :icon="faArrowDownAZ"
                  class="props-filter__item-icon"
                  aria-hidden="true"
                />
                <span class="props-filter__item-label">A-Z</span>
                <font-awesome-icon
                  v-if="sortColumn === SORT_MODES.PLAYER"
                  :icon="faCheck"
                  class="props-filter__check"
                  aria-hidden="true"
                />
              </button>
              <span class="props-filter__divider" aria-hidden="true" />
              <button
                type="button"
                class="props-filter__item"
                :class="{ 'is-active': view === 'player' }"
                role="menuitemradio"
                :aria-checked="view === 'player'"
                @click="toggleViewFilter('player')"
              >
                <font-awesome-icon
                  :icon="faUser"
                  class="props-filter__item-icon"
                  aria-hidden="true"
                />
                <span class="props-filter__item-label">By Player</span>
                <font-awesome-icon
                  v-if="view === 'player'"
                  :icon="faCheck"
                  class="props-filter__check"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                class="props-filter__item"
                :class="{ 'is-active': view === 'game' }"
                role="menuitemradio"
                :aria-checked="view === 'game'"
                @click="toggleViewFilter('game')"
              >
                <font-awesome-icon
                  :icon="faFootball"
                  class="props-filter__item-icon"
                  aria-hidden="true"
                />
                <span class="props-filter__item-label">By Game</span>
                <font-awesome-icon
                  v-if="view === 'game'"
                  :icon="faCheck"
                  class="props-filter__check"
                  aria-hidden="true"
                />
              </button>
              <template v-if="canClearFilter">
                <span class="props-filter__divider" aria-hidden="true" />
                <button
                  type="button"
                  class="props-filter__item props-filter__item--clear"
                  @click="clearFilter"
                >
                  <font-awesome-icon
                    :icon="faXmark"
                    class="props-filter__item-icon"
                    aria-hidden="true"
                  />
                  <span class="props-filter__item-label">Clear filter</span>
                </button>
              </template>
            </div>
          </div>
          <!-- PRO-252: chevron paddles appear only when the pill row
               overflows, giving mouse users a way to reach clipped pills
               (the row scrolls but its scrollbar is hidden, so without
               these the trailing pills look like they don't exist). The
               edge fade signals hidden content in the paddles' direction. -->
          <button
            v-if="presetsOverflow"
            type="button"
            class="scroll-paddle"
            :disabled="!canScrollLeft"
            aria-label="Scroll filters left"
            @click="scrollPresets(-1)"
          >
            <font-awesome-icon :icon="faChevronLeft" aria-hidden="true" />
          </button>
          <div
            ref="presetsRef"
            class="hero-search__presets"
            :class="{ 'fade-left': canScrollLeft, 'fade-right': canScrollRight }"
            role="tablist"
            aria-label="Prop presets"
            @scroll.passive="updatePresetsOverflow"
          >
            <button
              type="button"
              class="hero-preset text-nowrap"
              :class="{ active: view === 'popular' }"
              role="tab"
              :aria-selected="view === 'popular'"
              @click="selectView('popular')"
            >
              Popular
            </button>
            <span
              v-if="showCategoryMenu && sportTabs.length"
              class="hero-preset-divider"
              aria-hidden="true"
            />
            <button
              v-for="tab in sportTabs"
              :key="tab.name"
              type="button"
              class="hero-preset hero-preset--category text-nowrap"
              :class="{ active: activeTab === tab.name }"
              @click="selectCategory(tab.name)"
            >
              {{ tab.name }}
            </button>
          </div>
          <button
            v-if="presetsOverflow"
            type="button"
            class="scroll-paddle"
            :disabled="!canScrollRight"
            aria-label="Scroll filters right"
            @click="scrollPresets(1)"
          >
            <font-awesome-icon :icon="faChevronRight" aria-hidden="true" />
          </button>
        </div>
      </div>

      <spinner v-if="loading" />
      <div v-else-if="!sportFilteredGames.length" class="empty-state">
        No player props available right now.
      </div>
      <template v-else>
        <div v-if="committedView === 'popular'" class="props-list">
          <div v-if="!sortedFlatGames.length" class="empty-state">
            No player props available right now.
          </div>
          <div
            v-for="game in paginatedFlatGames"
            :key="game.id"
            class="all-props-row"
          >
            <span
              v-if="game.sport && selectedSport === 'all'"
              class="sport-badge"
              :title="game.sport"
            >
              <font-awesome-icon
                :icon="getFaSportIcon({ sport: game.sport, league: game.league })"
                class="sport-badge__icon"
              />
            </span>
            <game-props
              :initial-game="game"
              @make-bet="makeBet"
              @cancel-offer="cancelOffer"
              @edit-order="editOrder"
              @get-position="getPosition"
              @open-offers-modal="showOpenOffersModal"
            />
          </div>
        </div>
        <div
          v-else-if="showCategoryMenu && committedView === 'category'"
          class="props-list"
        >
          <div
            v-for="game in paginatedCategoryGames"
            :key="game.id"
            class="all-props-row"
          >
            <game-props
              :initial-game="game"
              @make-bet="makeBet"
              @cancel-offer="cancelOffer"
              @edit-order="editOrder"
              @get-position="getPosition"
              @open-offers-modal="showOpenOffersModal"
            />
          </div>
          <div v-if="!categoryGames.length" class="empty-state">
            No {{ committedTab }} props available right now.
          </div>
        </div>
        <div v-else-if="committedView === 'game'" class="player-view">
          <section
            v-for="group in paginatedGameGroups"
            :key="group.gameID"
            class="player-section"
            :class="{ 'is-open': openGames.has(group.gameID) }"
          >
            <button
              type="button"
              class="player-section-header"
              :aria-expanded="openGames.has(group.gameID)"
              @click="toggleGame(group.gameID)"
            >
              <span
                class="disclosure-chev"
                :class="{ open: openGames.has(group.gameID) }"
                aria-hidden="true"
              >
                &#9656;
              </span>
              <router-link
                :to="`/exchange-single/${group.gameID}`"
                class="game-link"
                @click.stop
              >
                <h3 class="section-title">{{ group.matchup }}</h3>
              </router-link>
              <span class="market-count">
                {{ group.start }} · {{ group.games.length }} markets
              </span>
            </button>
            <div v-if="openGames.has(group.gameID)" class="props-list">
              <game-props
                v-for="game in group.games"
                :key="`game-prop-${game.id}`"
                :initial-game="game"
                @make-bet="makeBet"
                @cancel-offer="cancelOffer"
                @edit-order="editOrder"
                @get-position="getPosition"
                @open-offers-modal="showOpenOffersModal"
              />
            </div>
          </section>
          <div v-if="!propsByGame.length" class="empty-state">
            No player props available right now.
          </div>
        </div>
        <div v-else class="player-view">
          <section
            v-for="group in paginatedPlayerGroups"
            :key="group.playerName"
            class="player-section"
            :class="{ 'is-open': openPlayers.has(group.playerName) }"
          >
            <button
              type="button"
              class="player-section-header"
              :aria-expanded="openPlayers.has(group.playerName)"
              @click="togglePlayer(group.playerName)"
            >
              <span
                class="disclosure-chev"
                :class="{ open: openPlayers.has(group.playerName) }"
                aria-hidden="true"
              >
                &#9656;
              </span>
              <h3 class="section-title">{{ group.playerName }}</h3>
              <span class="market-count">
                {{ group.games.length }}
                {{ group.games.length === 1 ? 'market' : 'markets' }}
              </span>
            </button>
            <div v-if="openPlayers.has(group.playerName)" class="props-list">
              <game-props
                v-for="game in group.games"
                :key="`player-prop-${game.id}`"
                :initial-game="game"
                @make-bet="makeBet"
                @cancel-offer="cancelOffer"
                @edit-order="editOrder"
                @get-position="getPosition"
                @open-offers-modal="showOpenOffersModal"
              />
            </div>
          </section>
          <div v-if="!propsByPlayer.length" class="empty-state">
            No player props available right now.
          </div>
        </div>
      </template>

      <div v-if="!loading && hasMore" class="load-more-wrap mt-3">
        <button
          type="button"
          class="load-more-btn"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
      </div>

      <div v-if="errors.length" class="partial-error mt-3">
        Some props could not be loaded. Showing available markets.
      </div>
    </div>

    <take-bet-modal
      v-if="showTakeBetModal"
      :key="modalGame?.id"
      :session="takeBetsession"
      :full-game="modalGame"
      @close-modal="closePropsTakeBetModal"
    />
    <new-offer-modal
      v-if="showNewOfferModal"
      :session="takeBetsession"
      :game="modalGame"
      @close-modal="showNewOfferModal = false"
    />
    <edit-order-modal
      v-if="showEditOfferModal"
      :session="editOrderSession"
      :full-game="editOrderGame"
      @cancel="cancelOffer"
      @close-modal="showEditOfferModal = false"
    />
    <my-positions-modal
      v-if="myPositionsModalShown"
      :position="myPositionsSelectedPosition"
      @close-modal="myPositionsModalShown = false"
    />
    <open-offers-modal
      v-if="openOffers"
      :game-id="openOffersGameID"
      @close-modal="openOffers = false"
    />
  </main>
</template>

<script>
/**
 * PRO-123: pure helpers only. ALL page state (popular payload, category
 * payloads, search results) is deliberately PER-VISIT — an earlier revision
 * cached payloads at module scope to survive remounts, and reconciling that
 * cache with the socket-mutated, route-wiped shared store produced a whole
 * family of defects (QA F1/R1/R2/R4: stale-odds snap-back, foreign-row
 * leaks, ended-game resurrection, blank-board revisits). Fresh visit =
 * fresh fetch. The only cross-visit memory is the store's removedGameIds
 * registry, which exists so an ended game is never re-seeded mid-session.
 */

// Mirrors PROPS_LEAGUE_TO_SPORT in core-api's getAllProps.js. searchGames
// rows may lack `sport` on the raw doc (getAllProps backfills it for exactly
// that reason), and a sportless row is invisible under every sport pill.
const PROPS_LEAGUE_TO_SPORT = {
  'NBA-PROPS': 'basketball',
  'NCAAB-PROPS': 'basketball',
  'MLB-PROPS': 'baseball',
  'NFL-PROPS': 'football',
  'NCAAF-PROPS': 'football',
  'NHL-PROPS': 'hockey',
};

function sportForPropsLeague(league) {
  return PROPS_LEAGUE_TO_SPORT[String(league || '').toUpperCase()];
}

function mergeUniqueById(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    for (const game of list || []) {
      const id = game && game.id != null ? String(game.id) : '';
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(game);
    }
  }
  return out;
}
</script>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import { useAPI } from '@/composables/useAPI';
import { removedGameIds, useExchangeStore } from '@/stores/exchange';
import { subscriptionManager } from '@/core/sockets/subscriptionManager';
import {
  getApiCategoriesForTab,
  getPropTabsForSport,
  matchesPropTab,
  propTabKeywords,
} from '@/utils/propTabs';
import {
  SORT_MODES,
  sortGroupsByMode,
  sortPropsList,
} from '@/utils/propSort';
import { stripPropFlags } from '@/core/utils';
import LeagueHeader from '@/components/global/header/LeagueHeader.vue';
import Spinner from '@/components/global/Spinner.vue';
import GameProps from '@/components/sportsbook/GameProps.vue';
import TakeBetModal from '@/components/betting/modals/TakeBetModal.vue';
import NewOfferModal from '@/components/betting/modals/NewOfferModal.vue';
import EditOrderModal from '@/components/betting/modals/EditOrderModal.vue';
import MyPositionsModal from '@/components/userBets/modals/MyPositionsModal.vue';
import OpenOffersModal from '@/components/userBets/modals/OpenOffersModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
import { faArrowDownAZ } from '@fortawesome/free-solid-svg-icons/faArrowDownAZ';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faFootball } from '@fortawesome/free-solid-svg-icons/faFootball';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { getFaSportIcon } from '@/core/getIcon.js';
import { PostHogUtils, normalizeSearchTerm } from '@/core/PostHogUtils';

const exchangeStore = useExchangeStore();
const route = useRoute();
// `/exchange/getAllProps` aggregates every prop league server-side in one
// request, sorts globally by matchedVolume, and returns a single payload.
// This replaces the old "fan out N parallel getOrderbookPaginated calls and
// stitch them client-side" pattern, which made the Popular sort visibly
// reshuffle as later leagues arrived.
const { get: getAllPropsRequest } = useAPI('/exchange/getAllProps');
const { get: searchGamesRequest } = useAPI('exchange/searchGames');
const { request: cancelOfferRequest } = useAPI('session/cancel/');

// 30 matches the backend's per-category pass cap (getAllProps
// PER_CATEGORY_PASS_CAP), so on a category pill one page = one full pass of
// that category, and each Load more click reveals exactly the next pass.
const PAGE_SIZE = 30;
const GROUP_PAGE_SIZE = 15;
// Soft cap on what we render at once — backend default is 200, max 500.
const ALL_PROPS_FETCH_LIMIT = 200;

const loading = ref(false);
// PRO-172: the page's game list lives in exchangeStore.games so that the
// shared price-stream handlers (processOrderUpdate / processGameUpdate /
// processMatchedVolumeUpdate) mutate it in place. A local ref here would
// be invisible to those handlers and the board would only update on
// manual refresh.
const games = computed(() => exchangeStore.games);
const errors = ref([]);
const selectedSport = ref('all');
const searchQuery = ref('');
// True only while the search INPUT is focused (not the pills), so the focus
// glow lights up just for typing — not when a pill button takes focus.
const searchFocused = ref(false);
// Default to the 'popular' flat list — that's the orientation the page used
// to ship with and reads as "show me everything ranked by best/most popular".
// 'player' / 'game' are opt-in groupings via the preset chips in the search
// bar; 'category' is the sport-specific stat filter.
const view = ref('popular');
const activeTab = ref('');
// Mobile snappiness: tapping a pill must repaint its selected state right away,
// but re-rendering the (heavy) GameProps list for the new view/tab is what
// costs time. If both happen in one flush the browser can't paint the pill
// highlight until the list finishes patching, so the old pill looks
// stuck-selected for a beat. So the PILLS read view/activeTab (update now) while
// the LIST + its computeds read committedView/committedTab, advanced on the next
// animation frame — the highlight paints first, the list catches up.
const committedView = ref('popular');
const committedTab = ref('');
let commitFrame = 0;
function commitViewSoon() {
  if (typeof requestAnimationFrame === 'undefined') {
    committedView.value = view.value;
    committedTab.value = activeTab.value;
    return;
  }
  if (commitFrame) cancelAnimationFrame(commitFrame);
  commitFrame = requestAnimationFrame(() => {
    commitFrame = 0;
    committedView.value = view.value;
    committedTab.value = activeTab.value;
  });
}
// PRO-186: column-header sort. `sortColumn` is null in the default Popular
// state; clicking a header cycles it through asc -> desc -> off. `sortDirection`
// is only meaningful while a column is active. Persists across pill / category
// changes within a session; resets on full reload (refs re-init on mount).
const sortColumn = ref(null); // null | SORT_MODES.PLAYER | SORT_MODES.STARTING
const sortDirection = ref('asc');
// "Filter by" dropdown. Sorts (Earliest / A-Z) drive sortColumn; groupings
// (By Player / By Game) drive the existing `view`. Selecting the active option
// in a group turns it off; Clear filter resets both back to Popular.
const filterMenuOpen = ref(false);
const filterMenuRef = ref(null);

// --- Props-navigation analytics (props-page-redesign decision) -------------
// PM wants to know how people navigate props — what leads to a bet and what
// doesn't. props_filter_changed records real changes to the three navigation
// controls (values are stable option keys, never display labels); the
// bet-side of the funnel is bet_modal_opened with source:'props' (see
// makeBet's store marker). Toggling a control OFF / re-tapping the active
// option is deliberately not an event — the decision needs "which options do
// people reach for", not every no-op tap.
const FILTER_SORT_VALUES = {
  [SORT_MODES.STARTING]: 'earliest',
  [SORT_MODES.PLAYER]: 'a_z',
};
const GROUPING_VALUES = { player: 'by_player', game: 'by_game' };

// Category pills are display names from the propTabKeywords catalog (bounded
// set) — normalize to snake_case keys so "Home Runs" is one row, not several.
function categoryKey(name) {
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toggleFilter(mode) {
  const activated = sortColumn.value !== mode;
  sortColumn.value = activated ? mode : null;
  sortDirection.value = 'asc';
  filterMenuOpen.value = false;
  if (activated) {
    PostHogUtils.capture('props_filter_changed', {
      control: 'sort',
      value: FILTER_SORT_VALUES[mode],
    });
  }
}
function toggleViewFilter(next) {
  if (view.value === next) {
    // Toggle the grouping off -> back to the flat list, keeping the active stat
    // tab if one is selected (only Popular clears the category).
    selectView(activeTab.value ? 'category' : 'popular');
  } else {
    selectView(next);
    PostHogUtils.capture('props_filter_changed', {
      control: 'grouping',
      value: GROUPING_VALUES[next],
    });
  }
  filterMenuOpen.value = false;
}
function clearFilter() {
  selectView('popular');
  sortColumn.value = null;
  sortDirection.value = 'asc';
  filterMenuOpen.value = false;
}
const anyFilterActive = computed(
  () => !!sortColumn.value || view.value === 'player' || view.value === 'game',
);
const canClearFilter = computed(
  () => anyFilterActive.value || !!activeTab.value,
);
function onDocumentClick(event) {
  if (!filterMenuOpen.value) return;
  if (filterMenuRef.value && !filterMenuRef.value.contains(event.target)) {
    filterMenuOpen.value = false;
  }
}
function onDocumentKeydown(event) {
  if (event.key === 'Escape') filterMenuOpen.value = false;
}
// PRO-252: overflow state for the preset pill row. The row scrolls with a
// hidden scrollbar, so when it overflows we render chevron paddles (and edge
// fades) as the visible affordance. canScrollLeft/Right drive both the
// paddles' disabled state and which edge fades out.
const presetsRef = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const presetsOverflow = computed(
  () => canScrollLeft.value || canScrollRight.value,
);
function updatePresetsOverflow() {
  const el = presetsRef.value;
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  // 2px tolerance absorbs sub-pixel rounding of scrollWidth/clientWidth.
  canScrollLeft.value = el.scrollLeft > 2;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
}
function scrollPresets(direction) {
  presetsRef.value?.scrollBy({ left: direction * 160, behavior: 'smooth' });
}
// ResizeObserver catches container width changes (window resize, sidebar
// toggles); the sportTabs watcher (registered below the sportTabs /
// showCategoryMenu declarations) catches content changes, which resize
// alone doesn't observe.
let presetsResizeObserver = null;
const secondaryMode = computed(() => sortColumn.value || SORT_MODES.POPULAR);
const visibleCardsCount = ref(PAGE_SIZE);
const visibleGroupsCount = ref(GROUP_PAGE_SIZE);
const showTakeBetModal = ref(false);
const showNewOfferModal = ref(false);
const showEditOfferModal = ref(false);
const takeBetsession = ref(null);
const modalGame = ref(null);
const editOrderSession = ref(null);
const editOrderGame = ref(null);
const openOffersGameID = ref('');
const openOffers = ref(false);

function showOpenOffersModal(gameID) {
  openOffersGameID.value = gameID;
  openOffers.value = true;
}
const myPositionsModalShown = ref(false);
const myPositionsSelectedPosition = ref(null);

// PRO-123: async continuations (category fetch, server search, popular
// refresh) must not touch the SHARED exchange store after this page
// unmounts. Flipped in onBeforeUnmount; every await re-checks it before
// committing.
let isDeparted = false;

// Per-visit payload holders (see the module-block note for why these are
// NOT cached across visits). The board is the union of the popular payload,
// fetched category payloads, and active search results — deduped by id.
// The TTL only dedupes in-session category pill re-clicks.
const ALL_PROPS_CACHE_TTL_MS = 30_000;
let popularGames = [];
const categoryPayloads = new Map(); // `${sport}::${tabName}` -> { games, at }
const categoryFetchesInFlight = new Map(); // key -> in-flight Promise (QA F4)
let searchResultGames = [];
// Server-side pagination state for the popular payload: the backend caps
// each (sport, category) per balanced pass and reports hasMore past the
// current window, so "Load more" can keep pulling pages (skip = rows held)
// instead of dead-ending at the first fetch.
const backendHasMore = ref(false);
const loadingMore = ref(false);

// Sports that have at least one *-PROPS league available, sourced from the
// nav payload so chips are visible immediately on first paint (before any
// game data resolves) and stay stable as users filter.
const availablePropsSports = computed(() => {
  const sportsAvailable =
    exchangeStore.sportsNavigations?.sportsAvailable || {};
  const seen = new Set();
  for (const [sport, leagues] of Object.entries(sportsAvailable)) {
    if (!Array.isArray(leagues)) continue;
    if (leagues.some((l) => String(l).toUpperCase().includes('PROPS'))) {
      seen.add(sport);
    }
  }
  // Union with any sports already loaded into games (covers edge cases where
  // a prop game came in for a sport not flagged in sportsAvailable yet).
  for (const game of games.value) {
    if (game.sport) seen.add(game.sport);
  }
  return [...seen].sort();
});

const sportFilters = computed(() => ['all', ...availablePropsSports.value]);

const propsHeaderFilters = computed(() =>
  sportFilters.value.map((sport) => ({
    key: sport,
    label: sport === 'all' ? 'POPULAR' : sport.toUpperCase(),
  })),
);

const searchPlaceholder = computed(() =>
  selectedSport.value === 'all'
    ? 'Search props, players, or games'
    : `Search ${selectedSport.value} props`,
);

const sportFilteredGames = computed(() => {
  let filtered =
    selectedSport.value === 'all'
      ? games.value
      : games.value.filter((game) => game.sport === selectedSport.value);
  const q = searchQuery.value.trim().toLowerCase();
  if (q) {
    filtered = filtered.filter((game) => {
      const haystack = [
        game.eventName,
        getPlayerName(game),
        game.participants?.[0]?.longName,
        game.participants?.[1]?.longName,
        game.participants?.[0]?.shortName,
        game.participants?.[1]?.shortName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  // "Most Popular" = most actually-traded volume globally. We deliberately
  // sort across all sports (no per-sport buckets) so the top of the list is
  // a stable answer to "what's hot right now" rather than an alphabetical
  // sport ordering that shuffles as more leagues stream in.
  //
  // openInterest is deliberately NOT an early sort key (ported from the 4cx
  // fix, 4cx-frontend 29ab83290). Longshot categories structurally carry the
  // most untaken risk (an MLB home-run prop books ~2.5x the OI of a
  // strikeout prop at the same stake), so OI-first turned the whole page
  // into one category. Instead, within a start-time slate we order by the
  // sport's category-pill catalog (baseball: Strikeouts, Pitching Outs,
  // Total Bases, then Home Runs), and only use OI to order markets inside
  // the same category.
  //
  // Sort keys, high→low priority:
  //   1. matchedVolume DESC (real money already traded)
  //   2. start ASC (sooner slates up first)
  //   3. category-catalog rank ASC (per game's own sport/league; unknown sinks)
  //   4. openInterest DESC (liquidity, within the same category)
  //   5. eventName alpha (deterministic tie-breaker)
  // propCategoryIndex keyword-matches the pill catalog, so compute it once
  // per game (not twice per comparison) before sorting.
  const categoryRank = new Map(
    filtered.map((game) => [game, propCategoryIndex(game)]),
  );
  return [...filtered].sort((a, b) => {
    const aVol = parseFloat(a.matchedVolume) || 0;
    const bVol = parseFloat(b.matchedVolume) || 0;
    if (aVol !== bVol) return bVol - aVol;

    const aStart = Date.parse(a.start) || Number.POSITIVE_INFINITY;
    const bStart = Date.parse(b.start) || Number.POSITIVE_INFINITY;
    if (aStart !== bStart) return aStart - bStart;

    const aRank = categoryRank.get(a);
    const bRank = categoryRank.get(b);
    if (aRank !== bRank) return aRank - bRank;

    const aOI = parseFloat(a.openInterest) || 0;
    const bOI = parseFloat(b.openInterest) || 0;
    if (aOI !== bOI) return bOI - aOI;

    return (a.eventName || '').localeCompare(b.eventName || '', undefined, {
      sensitivity: 'base',
    });
  });
});

const sportTabs = computed(() => {
  if (selectedSport.value === 'all') return [];
  // Filter games to this sport (ignore search text) — search shouldn't make
  // category pills appear/disappear from under the user mid-typing.
  const sportGames = games.value.filter(
    (g) => g.sport === selectedSport.value,
  );
  const leaguesForSport = new Set(
    sportGames.filter((g) => g.league).map((g) => String(g.league)),
  );
  const hasNcaaf = [...leaguesForSport].some((l) =>
    l.toUpperCase().includes('NCAA'),
  );
  const hasNonNcaaf = [...leaguesForSport].some(
    (l) => !l.toUpperCase().includes('NCAA'),
  );
  let catalog;
  // For football we may have both NFL and NCAAF loaded; union the catalogs so
  // every prop a user can see has a matching pill. Order: base sport first,
  // then NCAA-only categories appended in catalog order.
  if (selectedSport.value === 'football' && hasNcaaf && hasNonNcaaf) {
    const base = getPropTabsForSport(selectedSport.value);
    const ncaa = getPropTabsForSport(selectedSport.value, 'NCAAF');
    const seen = new Set(base.map((t) => t.name));
    catalog = [...base, ...ncaa.filter((t) => !seen.has(t.name))];
  } else {
    const leagueHint = hasNcaaf && !hasNonNcaaf ? 'NCAAF' : undefined;
    catalog = getPropTabsForSport(selectedSport.value, leagueHint);
  }
  // QA F5 (supersedes PRO-171's hide-empty): pills come from the static
  // catalog, not the loaded board. The popular payload is truncated, so a
  // category can exist server-side with zero loaded rows — hiding its pill
  // made the exhaustive server fetch unreachable, which was this ticket's
  // motivating case. An empty category renders the existing
  // "No {tab} props available" state after its fetch resolves.
  return catalog;
});

const showCategoryMenu = computed(
  () => selectedSport.value !== 'all' && sportTabs.value.length > 0,
);

// PRO-252: re-measure pill-row overflow when the pill set changes (sport
// switch adds/removes category pills). Registered here, below the sportTabs /
// showCategoryMenu declarations — watch() evaluates its sources immediately,
// so referencing them earlier in setup is a temporal-dead-zone crash.
watch([sportTabs, showCategoryMenu], () => {
  nextTick(updatePresetsOverflow);
});

const categoryGames = computed(() => {
  // Reads committedTab (not activeTab) so the list re-render is deferred one
  // frame behind the pill highlight — see committedTab above.
  if (!committedTab.value) return [];
  // QA F9: a row fetched BY this category (server propCategory contract)
  // belongs in the view even when its eventName misses the keyword catalog
  // (bare-token/wording asymmetries) — otherwise fetched rows are invisible
  // in their own category. Membership = keyword match OR payload id.
  const payload = categoryPayloads.get(
    `${selectedSport.value}::${committedTab.value}`,
  );
  const payloadIds = new Set(
    (payload?.games || []).map((g) => String(g.id)),
  );
  const filtered = sportFilteredGames.value.filter(
    (game) =>
      payloadIds.has(String(game.id)) ||
      matchesPropTab(game.eventName, selectedSport.value, committedTab.value, game.league),
  );
  // Popular preserves the existing sportFilteredGames volume ordering.
  if (secondaryMode.value === SORT_MODES.POPULAR) return filtered;
  return sortPropsList(filtered, {
    sport: selectedSport.value,
    league: filtered[0]?.league,
    mode: secondaryMode.value,
    direction: sortDirection.value,
  });
});

// PRO-186: Popular flat list now routes through the secondary sort. POPULAR
// returns sportFilteredGames untouched (existing volume-ranked output); a
// non-POPULAR header flips the same list to its alpha / start-time order.
// QA F8: with no active query, the Popular flat list shows the popular
// payload's rows only — otherwise its contents depend on which pills or
// searches were clicked earlier in the session. During a search the query
// itself is the ask to widen the universe, so merged rows stay in.
const popularFlatGames = computed(() => {
  if (searchQuery.value.trim()) return sportFilteredGames.value;
  const popularIds = new Set(
    popularGames.map((g) => (g && g.id != null ? String(g.id) : '')),
  );
  return sportFilteredGames.value.filter((g) => popularIds.has(String(g.id)));
});

const sortedFlatGames = computed(() => {
  if (secondaryMode.value === SORT_MODES.POPULAR) return popularFlatGames.value;
  return sortPropsList(popularFlatGames.value, {
    sport: selectedSport.value,
    league: popularFlatGames.value[0]?.league,
    mode: secondaryMode.value,
    direction: sortDirection.value,
  });
});
const paginatedFlatGames = computed(() =>
  sortedFlatGames.value.slice(0, visibleCardsCount.value),
);
const paginatedCategoryGames = computed(() =>
  categoryGames.value.slice(0, visibleCardsCount.value),
);

function totalForCurrentView() {
  // Bugbot (fix-forward round): the flat Popular list renders
  // popularFlatGames (F8), so hasMore/Load-more totals must count the same
  // list — counting sportFilteredGames left a permanent phantom "Load more".
  if (committedView.value === 'popular') return popularFlatGames.value.length;
  if (showCategoryMenu.value && committedView.value === 'category') {
    return categoryGames.value.length;
  }
  if (committedView.value === 'game') return propsByGame.value.length;
  return propsByPlayer.value.length;
}
function visibleForCurrentView() {
  if (committedView.value === 'popular') return paginatedFlatGames.value.length;
  if (showCategoryMenu.value && committedView.value === 'category') {
    return paginatedCategoryGames.value.length;
  }
  if (committedView.value === 'game') return paginatedGameGroups.value.length;
  return paginatedPlayerGroups.value.length;
}
// Whether pulling another popular page from the backend can grow the list
// the user is LOOKING AT (Bugbot: paging the global payload under a view it
// can't feed leaves the button visibly doing nothing).
//   - Search: no — search runs its own server fetch over the full universe;
//     paging the popular payload underneath it would just reshuffle results.
//   - Category pill whose exhaustive fetch (propsRequested) returned rows:
//     no — that list is already complete server-side; more popular pages
//     add nothing to it. When that fetch failed or came back empty (today's
//     unstamped-propCategory reality), popular pages are the ONLY way the
//     category grows, so paging stays on.
//   - Popular / By Player / By Game: yes — they render the popular union.
function backendPagingUsefulForView() {
  if (!backendHasMore.value) return false;
  if (searchQuery.value.trim()) return false;
  if (committedView.value === 'category' && committedTab.value) {
    const payload = categoryPayloads.get(
      `${selectedSport.value}::${committedTab.value}`,
    );
    if (payload?.games?.length) return false;
  }
  return true;
}

// The button also shows when the client holds nothing more to reveal but the
// backend still has rows this view can use — clicking then reveals AND
// fetches.
const hasMore = computed(
  () =>
    visibleForCurrentView() < totalForCurrentView() ||
    backendPagingUsefulForView(),
);

function loadMore() {
  visibleCardsCount.value += PAGE_SIZE;
  visibleGroupsCount.value += GROUP_PAGE_SIZE;
  // Once the reveal window has consumed everything fetched so far, pull the
  // next balanced page from the backend so the button never dead-ends while
  // the server still has rows this view can use.
  if (
    backendPagingUsefulForView() &&
    visibleForCurrentView() >= totalForCurrentView()
  ) {
    fetchMorePopular();
  }
}

// Guards the load-more continuation against a concurrent full refresh:
// fetchAllProps bumps the generation, and a page fetch started under an
// older generation discards its result instead of appending stale rows
// into the replaced payload / overwriting backendHasMore (Bugbot).
let popularFetchGeneration = 0;

// Next popular page. The skip cursor counts only rows the server still
// considers live: ended/expired rows stay in popularGames (the board keeps
// rendering what the user saw) but the server's list shrank by exactly
// those rows, so counting them would push the cursor PAST live rows and
// permanently drop them (Bugbot). removedGameIds is recorded at the store's
// removal sites, so subtracting it keeps the cursor aligned. Residual drift
// (rows reordered between requests) is covered by id-dedupe on append —
// a small overlap beats dropping rows.
async function fetchMorePopular() {
  if (loadingMore.value || !backendHasMore.value) return;
  const generation = popularFetchGeneration;
  loadingMore.value = true;
  try {
    const liveHeldCount = popularGames.filter(
      (g) => g && g.id != null && !removedGameIds.has(String(g.id)),
    ).length;
    const response = await getAllPropsRequest({
      limit: ALL_PROPS_FETCH_LIMIT,
      skip: liveHeldCount,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (isDeparted || generation !== popularFetchGeneration) return;
    backendHasMore.value = !!response?.hasMore;
    const fetched = Array.isArray(response?.games) ? response.games : [];
    const seen = new Set(
      popularGames.map((g) => (g && g.id != null ? String(g.id) : '')),
    );
    const fresh = fetched.filter(
      (g) => g && g.id != null && !seen.has(String(g.id)),
    );
    // An all-duplicate page means the cursor lagged a shifted dataset all
    // the way to rows we already hold — stop offering more rather than spin
    // on identical fetches.
    if (!fresh.length) {
      backendHasMore.value = false;
      return;
    }
    popularGames = [...popularGames, ...fresh];
    commitBoard();
  } catch (err) {
    if (!isDeparted && generation === popularFetchGeneration) {
      errors.value.push(err);
    }
  } finally {
    loadingMore.value = false;
  }
}

function resetPagination() {
  visibleCardsCount.value = PAGE_SIZE;
  visibleGroupsCount.value = GROUP_PAGE_SIZE;
}

// By Player / By Game group the active stat tab's props when a category is
// selected (so grouping respects the tab), otherwise all sport-filtered games.
const groupSourceGames = computed(() =>
  committedTab.value ? categoryGames.value : sportFilteredGames.value,
);

const propsByPlayer = computed(() => {
  const groups = new Map();
  for (const game of groupSourceGames.value) {
    const player = getPlayerName(game);
    if (!groups.has(player)) groups.set(player, []);
    groups.get(player).push(game);
  }
  // PRO-186: built[] is today's outer order (alpha by player). Layered
  // sortGroupsByMode reorders only when a column header is active; inner
  // game ordering inside each player is left untouched.
  const built = [...groups.entries()]
    .map(([playerName, gamesForPlayer]) => ({
      playerName,
      games: sortPlayerMarkets(gamesForPlayer),
    }))
    .sort((a, b) =>
      a.playerName.localeCompare(b.playerName, undefined, {
        sensitivity: 'base',
      }),
    );
  return sortGroupsByMode(built, {
    mode: secondaryMode.value,
    direction: sortDirection.value,
    getLabel: (g) => g.playerName,
    getEarliestStart: (g) =>
      g.games.reduce(
        (min, p) =>
          Math.min(min, Date.parse(p.start) || Number.POSITIVE_INFINITY),
        Number.POSITIVE_INFINITY,
      ),
  });
});

const paginatedPlayerGroups = computed(() =>
  propsByPlayer.value.slice(0, visibleGroupsCount.value),
);

const propsByGame = computed(() => {
  const groups = new Map();
  for (const game of groupSourceGames.value) {
    const key = game.parentGameID || game.id;
    if (!groups.has(key)) {
      groups.set(key, {
        gameID: key,
        matchup: matchupLabel(game),
        startRaw: game.start,
        start: formatStart(game.start),
        games: [],
      });
    }
    groups.get(key).games.push(game);
  }
  // PRO-186: built[] is today's outer order (start ASC, matchup alpha
  // tiebreaker). Layered sortGroupsByMode reorders only when a column
  // header is active.
  const built = [...groups.values()]
    .map((group) => ({
      ...group,
      games: sortPlayerMarkets(group.games),
    }))
    .sort((a, b) => {
      const aStart = new Date(a.startRaw).getTime() || Number.POSITIVE_INFINITY;
      const bStart = new Date(b.startRaw).getTime() || Number.POSITIVE_INFINITY;
      if (aStart !== bStart) return aStart - bStart;
      return a.matchup.localeCompare(b.matchup, undefined, {
        sensitivity: 'base',
      });
    });
  return sortGroupsByMode(built, {
    mode: secondaryMode.value,
    direction: sortDirection.value,
    getLabel: (g) => g.matchup,
    getEarliestStart: (g) =>
      new Date(g.startRaw).getTime() || Number.POSITIVE_INFINITY,
  });
});

const paginatedGameGroups = computed(() =>
  propsByGame.value.slice(0, visibleGroupsCount.value),
);

function matchupLabel(game) {
  const away = game.participants?.[0];
  const home = game.participants?.[1];
  const awayName = away?.longName || away?.shortName || 'Away';
  const homeName = home?.longName || home?.shortName || 'Home';
  return `${awayName} @ ${homeName}`;
}

function formatStart(start) {
  if (!start) return '';
  return dayjs(start).format('MMM DD · hh:mm A');
}

const openPlayers = ref(new Set());
const openGames = ref(new Set());

function togglePlayer(name) {
  const next = new Set(openPlayers.value);
  if (next.has(name)) next.delete(name);
  else next.add(name);
  openPlayers.value = next;
}

function toggleGame(id) {
  const next = new Set(openGames.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  openGames.value = next;
}

watch(
  propsByPlayer,
  (groups) => {
    const names = groups.map((g) => g.playerName);
    const filtered = new Set(
      [...openPlayers.value].filter((n) => names.includes(n)),
    );
    if (filtered.size === 0 && names.length > 0) filtered.add(names[0]);
    openPlayers.value = filtered;
  },
  { immediate: true },
);

watch(
  propsByGame,
  (groups) => {
    const ids = groups.map((g) => g.gameID);
    const filtered = new Set(
      [...openGames.value].filter((id) => ids.includes(id)),
    );
    if (filtered.size === 0 && ids.length > 0) filtered.add(ids[0]);
    openGames.value = filtered;
  },
  { immediate: true },
);

watch(selectedSport, () => {
  view.value = 'popular';
  activeTab.value = '';
  // Sport switch reloads the whole board, so commit the view/tab reset now.
  committedView.value = 'popular';
  committedTab.value = '';
  resetPagination();
  // Re-derive the board under the new sport so other sports' category
  // payloads drop out of the store and the price subscription immediately.
  commitBoard();
});

// Pagination resets when the rendered list actually changes: committedView /
// committedTab (not view / activeTab) so the reset lands with the deferred list.
watch([searchQuery, committedView, committedTab], () => {
  resetPagination();
});

// Props search is a client-side filter over already-loaded games, so we debounce
// and fire one analytics event per settled query (not per character). This is a
// directional "did this search find anything / lead to a bet" signal, so we keep
// it deliberately simple: at emit time we re-check the query is still the same
// non-empty term (so clearing/changing during the debounce records nothing) and
// read has_results from the currently rendered list. We don't try to gate on fetch
// freshness — the prop list doesn't change materially within a debounce, and the
// extra machinery isn't worth it for a breadcrumb.
let propsSearchTimer = null;
// Term whose analytics emit is deferred because props were still loading when
// the debounce fired. Flushed by the loading watcher once data settles, so a
// search typed during the initial fetch is neither dropped nor logged as a
// false miss against an empty (still-loading) list.
let pendingSearchTerm = null;

function emitPropsSearch(term) {
  // has_results must reflect the list the user actually sees: the category
  // view renders categoryGames (a category-filtered subset), while every
  // other view derives from sportFilteredGames (the popular flat list falls
  // back to it whenever a search is active). Reading the broader list in
  // category view would report "results" for games the page isn't showing.
  // Gate on committedTab (not view/activeTab): categoryGames keys off the
  // deferred committedTab, so reading view here would, for one frame after a
  // pill tap, treat the page as category view while categoryGames is still
  // empty — a false has_results:false. committedTab keeps the two in sync.
  const visibleGames =
    showCategoryMenu.value && committedTab.value
      ? categoryGames.value
      : sportFilteredGames.value;
  const hasResults = visibleGames.length > 0;
  // Only log the term on a miss (discovery gap). normalize can return null
  // (e.g. whitespace-only), so only attach when truthy.
  const missTerm = hasResults ? null : normalizeSearchTerm(term);
  PostHogUtils.capture('game_search_performed', {
    source: 'all_props',
    has_results: hasResults,
    ...(missTerm && { search_term: missTerm }),
  });
}

watch(searchQuery, (value) => {
  const term = (value || '').trim();
  if (propsSearchTimer) clearTimeout(propsSearchTimer);
  pendingSearchTerm = null;
  if (!term) {
    // PRO-123: query cleared — drop server search results from the board.
    if (searchResultGames.length) {
      searchResultGames = [];
      commitBoard();
    }
    return;
  }
  propsSearchTimer = setTimeout(async () => {
    if ((searchQuery.value || '').trim() !== term) return;
    // PRO-123: the popular payload is truncated server-side, so a client-only
    // filter can miss props that exist. Ask the global search endpoint (the
    // same one the main search bar uses — see Exchange/Index.vue), keep only
    // prop-league rows, and merge them into the board so the existing client
    // filter can find them. Fail-soft: on error the search bar degrades to
    // filtering already-loaded games (pre-PRO-123 behavior). Analytics run
    // after the merge so has_results reflects what the user actually sees.
    try {
      const response = await searchGamesRequest({
        sportRequested: 'all',
        leagueRequested: 'all',
        searchValue: term,
      });
      if (isDeparted) return;
      if ((searchQuery.value || '').trim() !== term) return;
      searchResultGames = (response?.games || [])
        // Prop rows only — searchGames also returns parents of matched props.
        .filter(
          (g) => g.league && String(g.league).toUpperCase().includes('PROPS'),
        )
        // Exclude aggregator/shell docs (they'd render as bogus "Unknown
        // Player" cards). propCategory is the primary discriminator, but it
        // must not be a hard requirement: the field only exists in newer
        // backend payloads AND on docs whose ingestion set it — either gap
        // silently killed the entire search merge in production. Every real
        // prop row carries an eventName (it's the string search matched on);
        // shells don't. Accept either signal.
        .filter((g) => g.propCategory || g.eventName)
        // Raw docs may lack `sport` (getAllProps backfills it server-side for
        // the same reason); a sportless row is invisible under sport pills.
        .map((g) => (g.sport ? g : { ...g, sport: sportForPropsLeague(g.league) }));
      commitBoard();
    } catch (err) {
      // fall through to the analytics capture with client-only results
    }
    if (isDeparted) return;
    // Re-check the term after the await as well: the query may have changed
    // while the request was in flight (the error path skipped the in-try
    // recheck), and emitting for a stale term would mislabel the analytics.
    if ((searchQuery.value || '').trim() !== term) return;
    // While props are still loading, an empty list is not a real miss — the
    // data just hasn't arrived. Defer the emit until loading settles instead
    // of dropping it (the watcher below flushes it).
    if (loading.value) {
      pendingSearchTerm = term;
      return;
    }
    emitPropsSearch(term);
  }, 800);
});

// Flush a search whose emit was deferred while props were loading, once load
// settles — but only if the user hasn't changed the query in the meantime.
watch(loading, (isLoading) => {
  if (isLoading || !pendingSearchTerm) return;
  const term = pendingSearchTerm;
  pendingSearchTerm = null;
  if ((searchQuery.value || '').trim() !== term) return;
  emitPropsSearch(term);
});

// Cancel any pending search-analytics timer on unmount so we don't emit
// game_search_performed for a page the user has already navigated away from.
onBeforeUnmount(() => {
  isDeparted = true;
  // The attribution marker belongs to this page visit. Identity matching in
  // TakeBetModal already prevents mis-attribution, but don't leave a stale
  // session object pinned in the shared store after departure.
  exchangeStore.setPropsBetContext(null);
  if (propsSearchTimer) clearTimeout(propsSearchTimer);
  pendingSearchTerm = null;
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', onDocumentClick, true);
    document.removeEventListener('keydown', onDocumentKeydown);
  }
  if (presetsResizeObserver) {
    presetsResizeObserver.disconnect();
    presetsResizeObserver = null;
  }
  if (commitFrame && typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(commitFrame);
  }
});

// PRO-186: secondary sort persists across main-pill and category-tab
// changes within the same page session. It only resets on full reload
// (the ref re-initialises on component mount).

function onSelectSport(sport) {
  selectedSport.value = sport;
}

function searchForEvent(value) {
  searchQuery.value = typeof value === 'string' ? value : '';
}

function selectView(next) {
  view.value = next;
  // Only Popular clears the active stat tab. Grouping (player / game) keeps it
  // so By Player / By Game regroup the selected category instead of dropping it.
  if (next === 'popular') activeTab.value = '';
  commitViewSoon();
}

function selectCategory(name) {
  // Real changes only — re-tapping the active pill (cache refresh) is not a
  // navigation choice.
  if (name !== activeTab.value) {
    PostHogUtils.capture('props_filter_changed', {
      control: 'category',
      value: categoryKey(name),
    });
  }
  activeTab.value = name;
  // Keep the current grouping if one is active (regroup the new tab); otherwise
  // show the flat category list.
  if (view.value !== 'player' && view.value !== 'game') view.value = 'category';
  commitViewSoon();
  // fetchCategoryProps' cache-hit path commits the board SYNCHRONOUSLY, which
  // would block the just-tapped pill from painting until the heavy rebuild +
  // re-render finish (the mobile pill-switch lag). Defer it a frame so the
  // highlight lands immediately; the board/list update follows.
  const sport = selectedSport.value;
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(() => {
      if (!isDeparted) fetchCategoryProps(sport, name);
    });
  } else {
    fetchCategoryProps(sport, name);
  }
}

// PRO-123: fetch one category exhaustively. Additive and fail-soft: on
// error or empty result the pill degrades to filtering the popular payload
// (pre-PRO-123 behavior). Cached per sport+tab with the same TTL as the
// popular payload.
async function fetchCategoryProps(sport, tabName) {
  if (sport === 'all') return;
  const apiCategories = getApiCategoriesForTab(sport, tabName);
  if (!apiCategories.length) return;
  const key = `${sport}::${tabName}`;
  const cached = categoryPayloads.get(key);
  if (cached && Date.now() - cached.at < ALL_PROPS_CACHE_TTL_MS) {
    // Cache hit still recommits: the store may have been wiped since this
    // payload was fetched (a failed popular refresh blanks the board), and
    // returning without a commit would leave held rows invisible until the
    // TTL expires. commitBoard is idempotent — no-op when nothing changed.
    commitBoard();
    return;
  }
  // QA F4: duplicate pill clicks share one in-flight Promise per key — but
  // every caller awaits it and then runs its OWN guarded commit, so no
  // click's commit is silently swallowed by another's early return.
  let flight = categoryFetchesInFlight.get(key);
  const isFlightCreator = !flight;
  if (!flight) {
    flight = (async () => {
      const response = await getAllPropsRequest({
        // Backend max is 500 (vs the 200 the popular fetch uses). A single
        // category on a single sport fits well inside that, so the category
        // view is exhaustive even for wide categories like MLB Home Runs.
        limit: 500,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sportRequested: sport,
        propsRequested: apiCategories,
      });
      const fetched = Array.isArray(response?.games) ? response.games : [];
      categoryPayloads.set(key, { games: fetched, at: Date.now() });
    })().finally(() => {
      categoryFetchesInFlight.delete(key);
    });
    categoryFetchesInFlight.set(key, flight);
  }
  try {
    await flight;
    // Never touch the shared store after unmount.
    if (isDeparted) return;
    // The board is a union, so committing is safe even if the user has
    // already switched pills mid-flight.
    commitBoard();
  } catch (err) {
    if (isDeparted) return;
    // Every waiter shares the flight, so only its creator records the
    // failure — one failed fetch, one error entry.
    if (isFlightCreator) errors.value.push(err);
  }
}

function getPlayerName(game) {
  if (game.playerName) return game.playerName;
  const eventName = stripPropFlags(game.eventName || '').trim();
  if (!eventName) return 'Unknown Player';

  const parenIndex = eventName.indexOf('(');
  if (parenIndex > 0) return eventName.slice(0, parenIndex).trim();

  // Format B: "PLAYER NAME TOTAL STAT" — strip the longest matching stat tail
  // from the propTabKeywords catalog, then drop a dangling "TOTAL" token.
  const sport = game.sport;
  const league = game.league;
  const isNcaaf =
    sport === 'football' &&
    typeof league === 'string' &&
    league.toUpperCase().includes('NCAA');
  const sportKey = isNcaaf ? 'ncaaFootball' : sport;
  const tabsForSport = propTabKeywords[sportKey] || {};
  const allKeywords = Object.values(tabsForSport).flat();
  const upper = eventName.toUpperCase();
  const tail = allKeywords
    .filter((kw) => !kw.startsWith('('))
    .filter((kw) => upper.endsWith(kw))
    .sort((a, b) => b.length - a.length)[0];

  if (tail) {
    let player = eventName.slice(0, eventName.length - tail.length).trim();
    player = player.replace(/\s+TOTAL$/i, '').trim();
    return player || eventName;
  }

  // Positional fallback for stats not in the propTabKeywords catalog (e.g.
  // ingestion adds a new stat before propTabs.js is updated). Take everything
  // before the first standalone TOTAL token; if no TOTAL appears, take the
  // leading 1-3 ALL-CAPS tokens (typical player-name shape).
  const totalMatch = eventName.match(/^(.+?)\s+TOTAL\b/i);
  if (totalMatch && totalMatch[1].trim()) {
    return totalMatch[1].trim();
  }
  const tokens = eventName.split(/\s+/);
  const leadingCaps = [];
  for (const tok of tokens) {
    if (/^[A-Z][A-Z'.\-]*$/.test(tok) && leadingCaps.length < 3) {
      leadingCaps.push(tok);
    } else {
      break;
    }
  }
  if (leadingCaps.length >= 1) {
    return leadingCaps.join(' ');
  }
  return eventName.replace(/\s+/g, ' ');
}

function propCategoryIndex(game) {
  const tabs = getPropTabsForSport(game.sport, game.league);
  const index = tabs.findIndex((tab) =>
    matchesPropTab(game.eventName, game.sport, tab.name, game.league),
  );
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}

function sortPlayerMarkets(list) {
  return [...list].sort((a, b) => {
    const aRank = propCategoryIndex(a);
    const bRank = propCategoryIndex(b);
    if (aRank !== bRank) return aRank - bRank;
    return (a.eventName || '').localeCompare(b.eventName || '', undefined, {
      sensitivity: 'base',
    });
  });
}

async function ensureSportsData() {
  if (!exchangeStore.sportsNavigations?.sportsAvailable) {
    await exchangeStore.getSportsData('basketball');
  }
}

function applySportFromQuery() {
  const sportParam = route.query.sport
    ? String(route.query.sport).toLowerCase()
    : '';
  if (!sportParam) return;
  if (sportParam === 'all') {
    selectedSport.value = 'all';
    return;
  }
  if (availablePropsSports.value.includes(sportParam)) {
    selectedSport.value = sportParam;
  }
}

// Rebuild the board from every payload we currently hold. commitGames stays
// the single writer into exchangeStore.games and the price-stream
// subscription (PRO-172).
//
// QA F1: the store's socket handlers REPLACE game objects with fresh-odds
// copies and SPLICE ended games out, while the payload holders keep
// fetch-time snapshots. Committing snapshots over live rows would snap the
// whole board back to fetch-time odds and resurrect ended games. So:
//   - a row already in the store is re-committed as its LIVE store object;
//   - a payload object only seeds rows the store doesn't hold yet;
//   - a row the app saw end/expire (removedGameIds, recorded at the store's
//     removal sites) is never re-seeded from a cached payload;
//   - rows the socket added that no payload knows about are preserved.
// QA F2: fresher payloads first — first-occurrence-wins dedup must keep the
// just-fetched copy of an overlapping game, not the older popular snapshot.
// QA F3: category payloads older than the TTL no longer (re)introduce rows;
// the TTL gates display, not just refetch.
function commitBoard() {
  const now = Date.now();
  const live = new Map();
  for (const g of exchangeStore.games) {
    if (g && g.id != null) live.set(String(g.id), g);
  }
  // Only the selected sport's category payloads join the union — an NBA
  // Points payload fetched seconds ago must not keep basketball rows (and a
  // phantom sport chip, and off-sport price subscriptions) alive on the MLB
  // view for the length of the TTL.
  const sportPrefix = `${selectedSport.value}::`;
  const freshCategoryLists = [...categoryPayloads.entries()]
    .filter(([key, c]) => key.startsWith(sportPrefix) && now - c.at < ALL_PROPS_CACHE_TTL_MS)
    .map(([, c]) => c.games);
  const union = mergeUniqueById(
    searchResultGames,
    ...freshCategoryLists,
    popularGames,
  );
  const merged = [];
  const inUnion = new Set();
  for (const g of union) {
    const id = String(g.id);
    inUnion.add(id);
    const liveGame = live.get(id);
    if (liveGame) {
      merged.push(liveGame);
      continue;
    }
    // QA R4 (replaces the committedIds heuristic, which couldn't tell an
    // ended-game splice from a route-change store wipe and blanked the board
    // on cache-fresh revisits): never seed a row the app saw end/expire.
    if (removedGameIds.has(id)) continue;
    merged.push(g);
  }
  // QA R1: the store is SHARED across routes — within the popular cache TTL
  // the cache-fresh path never wipes it, so it can still hold the previous
  // route's games (exchange league, single game). Preserve only prop rows:
  // socket-added props stay, foreign-route games never leak into the props
  // board or its setFeatured subscription.
  for (const [id, liveGame] of live) {
    if (inUnion.has(id)) continue;
    const league = String(liveGame?.league || '').toUpperCase();
    if (league.includes('PROPS')) merged.push(liveGame);
  }
  commitGames(merged);
}

// PRO-172: write fetched/cached games into exchangeStore.games and refresh
// the price-stream subscription to the new game ID set, so live order /
// game / matched-volume updates land on the page without manual refresh.
function commitGames(merged) {
  exchangeStore.games.length = 0;
  if (merged.length) {
    exchangeStore.pushGames(merged);
  }
  const ids = merged
    .map((g) => (g && g.id != null ? String(g.id) : ''))
    .filter(Boolean);
  subscriptionManager.setFeatured(ids);
}

async function fetchAllProps() {
  errors.value = [];
  // Every fetch (mount or header refresh) blanks and refetches — no
  // cross-visit cache to reconcile (see the module-block note). Bumping the
  // generation invalidates any in-flight load-more page: its result would
  // append rows from the pre-refresh ordering into the replaced payload.
  popularFetchGeneration += 1;
  commitGames([]);
  loading.value = true;

  try {
    await ensureSportsData();
    applySportFromQuery();
  } catch (err) {
    if (isDeparted) return;
    errors.value.push(err);
    // Fail-soft: render whatever this visit still holds (prior popular
    // payload on a header refresh, cached categories, active search)
    // instead of leaving the wiped board blank.
    commitBoard();
    loading.value = false;
    return;
  }
  if (isDeparted) return;

  // Single request, single sort, single render. Backend already grouped,
  // tagged with sport/league, and globally sorted by matchedVolume.
  let response;
  try {
    response = await getAllPropsRequest({
      limit: ALL_PROPS_FETCH_LIMIT,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch (err) {
    if (isDeparted) return;
    errors.value.push(err);
    // Same fail-soft recommit as the ensureSportsData catch above.
    commitBoard();
    loading.value = false;
    return;
  }

  const merged = Array.isArray(response?.games) ? response.games : [];

  popularGames = merged;
  backendHasMore.value = !!response?.hasMore;
  // Never touch the shared store after unmount.
  if (isDeparted) return;
  commitBoard();
  loading.value = false;
}

// Category attributed to a bet click: the active pill wins; on the flat
// views, fall back to the prop's own category from the keyword catalog
// (bounded set, same matcher the pills filter with). null when neither
// resolves — the modal omits the property rather than send junk.
function propCategoryForGame(game) {
  if (activeTab.value) return categoryKey(activeTab.value);
  const tabs = getPropTabsForSport(game?.sport, game?.league);
  const hit = tabs.find((tab) =>
    matchesPropTab(game?.eventName, game?.sport, tab.name, game?.league),
  );
  return hit ? categoryKey(hit.name) : null;
}

function makeBet(data) {
  const val = Array.isArray(data.session) ? data.session[0] : data.session;
  takeBetsession.value = val;
  modalGame.value = data.game;
  if (val?.source === 'fillerData') {
    showNewOfferModal.value = true;
    return;
  }
  // Props-funnel attribution: stamp the store marker (same pattern as
  // best_offers) so TakeBetModal's bet_modal_opened carries source:'props'
  // plus the navigation state at click time. Written right before the modal
  // mounts; matched by session identity; cleared when this page's modal
  // closes.
  exchangeStore.setPropsBetContext({
    session: val,
    category: propCategoryForGame(data.game),
    grouping: GROUPING_VALUES[view.value] || 'none',
  });
  showTakeBetModal.value = true;
}

function closePropsTakeBetModal() {
  showTakeBetModal.value = false;
  exchangeStore.setPropsBetContext(null);
}

async function cancelOffer(sessionID) {
  await cancelOfferRequest({ sessionID });
}

function editOrder(data) {
  editOrderSession.value = data.session;
  editOrderGame.value = data.game;
  showEditOfferModal.value = true;
}

function getPosition(position) {
  myPositionsSelectedPosition.value = position;
  myPositionsModalShown.value = true;
}

onMounted(() => {
  // PRO-172: processOrderUpdate / processGameUpdate / processMatchedVolumeUpdate
  // in exchangeStore branch on singleGameOrExchange. AllProps lives in the
  // multi-game "exchange" array, so claim that mode before the first frame
  // arrives.
  exchangeStore.setSingleGameOrExchange('exchange');
  // Close the "Filter by" dropdown on any outside click / Escape. Capture phase
  // so a header popup trigger that calls stopPropagation can't leave both open.
  if (typeof document !== 'undefined') {
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onDocumentKeydown);
  }
  // PRO-252: track pill-row overflow from first paint onward.
  if (typeof ResizeObserver !== 'undefined') {
    presetsResizeObserver = new ResizeObserver(updatePresetsOverflow);
    if (presetsRef.value) presetsResizeObserver.observe(presetsRef.value);
  }
  nextTick(updatePresetsOverflow);
  // Webfont swap changes pill text width (scrollWidth) without resizing the
  // row's box, so neither ResizeObserver nor the watchers fire — re-measure
  // once the fonts land.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(updatePresetsOverflow);
  }
  PostHogUtils.capture('props_viewed', { source: 'all_props' });
  // Stamp the profile so PostHog can compute "% of users who ever looked at
  // props", independent of refresh/revisit counts.
  PostHogUtils.syncSettings({ has_viewed_props: true });
  fetchAllProps();
});

// Intentionally no onBeforeUnmount cleanup of the price-stream subscription.
// vue-router fires `afterEach` (which runs `applyRouteSubscription` for the
// destination) BEFORE the leaving component's onBeforeUnmount, so any
// `subscriptionManager.clear()` here would wipe the subscription the next
// route just installed. `applyRouteSubscription` is the sole owner of
// route-transition subscription state; we follow the same pattern as the
// Exchange and SingleGame views.
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.all-props-page {
  min-height: 100vh;
}

/* Central search bar: input on the left, view/category pills embedded on the
   right. On wide screens it reads as a single bar; on narrow screens the
   presets wrap to a second line so the input never gets squeezed below ~6
   characters. */
.hero-search {
  display: flex;
  align-items: center;
  gap: 8px;
  // Fill the container so the bar lines up with the prop rows below it.
  width: 100%;
  padding: 6px 10px 6px 14px;
  background: main.$white_01;
  border: 1px solid main.$blue-901_01;
  border-radius: 999px;
  box-shadow: 0 1px 3px 0 main.$blue-1200;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  // Only while the search input is focused (see is-searching flag), not when a
  // pill button takes focus. A subtle border light-up, no background fill.
  &.is-searching {
    border-color: main.$color-primary;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.18);
  }

  &__icon {
    // Match the placeholder ("Search") colour so the icon reads as part of
    // the same prompt rather than a louder accent.
    color: main.$grey-2700;
    font-size: 19px;
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    min-width: 80px;
    border: none;
    background: transparent;
    color: main.$color-primary_01;
    // 16px minimum so iOS Safari doesn't zoom the viewport on focus, and so
    // the placeholder / typed text read larger to match the taller bar.
    font-size: 16px;
    line-height: 1.3;
    outline: none;

    &::placeholder {
      color: main.$grey-2700;
    }
  }

  &__clear {
    align-items: center;
    background: transparent;
    border: none;
    border-radius: 999px;
    color: main.$grey-2700;
    cursor: pointer;
    display: inline-flex;
    height: 24px;
    justify-content: center;
    padding: 0;
    width: 24px;

    &:hover {
      background: rgba(99, 102, 241, 0.1);
      color: main.$color-primary;
    }
  }

  // Wraps the Filter by dropdown + the scrollable preset pills as one group
  // to the right of the search input; the divider separates it from the input.
  &__controls {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-left: 4px;
    padding-left: 8px;
    border-left: 1px solid main.$blue-901_01;
    max-width: 60%;
  }

  &__presets {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    // Invisible scrollbar so it reserves no vertical space (keeps the pills
    // aligned with the Filter by pill); still scrollable via trackpad/drag.
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }

    // PRO-252: fade the edge that still has pills hidden beyond it, in
    // concert with the chevron paddles. Classes driven by scroll position.
    // Both mask properties written out — no autoprefixer in this build
    // (same as LeagueGamesStrip.vue).
    &.fade-right {
      mask-image: linear-gradient(
        to right,
        black 0,
        black calc(100% - 20px),
        transparent
      );
      -webkit-mask-image: linear-gradient(
        to right,
        black 0,
        black calc(100% - 20px),
        transparent
      );
    }

    &.fade-left {
      mask-image: linear-gradient(to right, transparent, black 20px, black 100%);
      -webkit-mask-image: linear-gradient(
        to right,
        transparent,
        black 20px,
        black 100%
      );
    }

    &.fade-left.fade-right {
      mask-image: linear-gradient(
        to right,
        transparent,
        black 20px,
        black calc(100% - 20px),
        transparent
      );
      -webkit-mask-image: linear-gradient(
        to right,
        transparent,
        black 20px,
        black calc(100% - 20px),
        transparent
      );
    }
  }
}

@media (max-width: 768px) {
  .hero-search {
    flex-wrap: wrap;
    border-radius: 16px;
    // Tight at the top (search area); the small bottom padding gives the
    // preset pills a little breathing room without growing the input area.
    padding: 0 10px 4px;
    // Very small column gap so "Search" sits right next to the icon; no row
    // gap so the divider rides right up under the input.
    column-gap: 1px;
    row-gap: 0;

    &__input {
      // Keep at 16px on mobile too: prevents iOS focus-zoom and keeps the
      // text sized to the larger bar.
      font-size: 16px;
      // Shorter line box pulls the divider up closer under the search text.
      line-height: 1.15;
    }

    // The Filter by dropdown + pills now live in __controls, so the mobile
    // full-width second-row treatment belongs here (not on __presets).
    &__controls {
      max-width: 100%;
      width: 100%;
      margin-left: 0;
      margin-top: 0;
      padding-left: 0;
      border-left: none;
      border-top: 1px solid main.$blue-901_01;
      // Equal space above (to the divider) and below (to the box edge).
      padding-top: 4px;
      gap: 8px;
    }

    &__presets {
      // A little more room between the pills (Popular / Total Strikeouts / ...).
      gap: 8px;
    }
  }

  .hero-preset {
    font-size: 11px;
    padding: 4px 12px;
  }

  /* The page container default margins are too generous on phones — let
     each card edge ride closer to the screen edge. */
  .all-props-page .container {
    padding-left: 8px;
    padding-right: 8px;
  }
}

[theme='night'] .hero-search {
  background: rgba(16, 20, 48, 0.6);
  border-color: rgba(134, 144, 219, 0.25);

  // While typing (search input focused only): light up a bit around the
  // border with a light grey — no background fill, and not when a pill is
  // clicked. Applies at all breakpoints.
  &.is-searching {
    border-color: rgba(198, 205, 235, 0.55);
    box-shadow: 0 0 0 3px rgba(198, 205, 235, 0.14);
  }

  &__icon {
    // Same colour as the "Search" placeholder text on dark.
    color: rgba(215, 219, 255, 0.55);
  }

  &__input {
    color: main.$white;

    &::placeholder {
      color: rgba(215, 219, 255, 0.55);
    }
  }

  &__clear {
    color: rgba(215, 219, 255, 0.65);

    &:hover {
      background: rgba(148, 158, 255, 0.18);
      color: main.$white;
    }
  }
}

/* PRO-186: reuse the .games-header treatment from Sportsbook/Index.vue so
   the player-prop sort row matches the existing market-list table header
   visually and column-wise. Inner buttons are transparent — colour comes
   from .games-header — and only flip to white + bold on the active sort.
   Unlike Sportsbook, the player-prop sort row stays visible on mobile so
   users on phones can still tap to re-sort. Bootstrap's col widths
   (col-2 / col-6) keep the two labels on a single line at narrow widths. */
.games-header {
  color: main.$color-primary_01;
  font-size: small;
  font-weight: lighter;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  background: main.$transparent_04;
  margin-bottom: main.$padding-01;
}

.games-header__sort {
  align-items: center;
  background: transparent;
  border: 0;
  padding: 4px 6px;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  gap: 6px;
  min-height: 28px;
  // Kills iOS Safari's 300ms tap delay and double-tap-zoom on the
  // header so cycle clicks register on the first tap.
  touch-action: manipulation;
  transition: font-weight 0.15s ease;

  &:hover {
    font-weight: 600;
  }

  // Active state communicates via weight + arrow opacity only. The earlier
  // approach used `color: main.$white` which is literally `#ffffff` in
  // light theme — pure white on the white page background, invisible.
  // Inheriting the theme's text colour means the bolder weight pops in
  // both themes without any [theme='night'] override needed.
  &.is-active {
    font-weight: 700;
  }
}

/* Variant B2 indicator: stacked up/down chevrons that are always rendered.
   Both arrows are dim by default; the active direction brightens via
   opacity (not colour) so it stays readable in both light and night
   themes. Keeps column width stable across all 3 states (off/asc/desc). */
.arr-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-size: 10px;
  line-height: 0.85;

  .arr-up,
  .arr-down {
    opacity: 0.35;
    transition: opacity 0.12s ease;
  }

  &.asc .arr-up {
    opacity: 1;
  }

  &.desc .arr-down {
    opacity: 1;
  }
}

@media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
  // On mobile drop the bootstrap col widths and just space the two labels
  // evenly across the row — there's no card grid to align with at this
  // size, so column-aligned widths only crowd "Start Time" into a 16% slot.
  // The col itself becomes the tap target (via the inner button stretching
  // to fill it) so users don't have to land precisely on the small label.
  .games-header.row {
    justify-content: space-around;

    > [class*='col-'] {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
      padding-left: 0;
      padding-right: 0;
    }
  }

  .games-header__sort {
    padding: 10px 14px;
    font-size: 12px;
    min-height: 40px; // 40-44px tap target per WCAG / iOS HIG
    white-space: nowrap;
  }
}

/* PRO-252: chevron paddles flanking the preset pill row. Rendered only while
   the row overflows; the paddle whose direction has nothing left to reveal
   goes to the disabled state. Matches the Filter-by trigger's grey treatment
   so they read as part of the same control cluster. */
.scroll-paddle {
  align-items: center;
  background-color: rgba(128, 137, 163, 0.14);
  border: none;
  border-radius: 999px;
  color: main.$color-primary_01;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 10px;
  height: 22px;
  justify-content: center;
  touch-action: manipulation;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;
  width: 22px;

  &:hover:not(:disabled) {
    background-color: rgba(128, 137, 163, 0.28);
  }

  &:disabled {
    cursor: default;
    opacity: 0.25;
  }
}

[theme='night'] .scroll-paddle {
  background-color: rgba(198, 205, 235, 0.1);
  color: rgba(215, 219, 255, 0.85);

  &:hover:not(:disabled) {
    background-color: rgba(198, 205, 235, 0.18);
    color: main.$white;
  }
}

/* The paddles exist for mouse users; on touch the row swipes natively and
   the paddles just crowd the pills, so hide them (edge fades still signal
   clipped content). */
@media (max-width: 768px) {
  .scroll-paddle {
    display: none;
  }
}

/* "Filter by" dropdown pill + menu (ported from 4cx). A light-grey pill that
   sits to the left of Popular; opens a menu with sort + grouping toggles. */
.props-filter {
  position: relative;
  flex-shrink: 0;
}

.props-filter__trigger {
  align-items: center;
  background-color: rgba(128, 137, 163, 0.14);
  border: 1px solid transparent;
  border-radius: 999px;
  color: main.$color-primary_01;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 5px;
  padding: 4px 10px;
  touch-action: manipulation;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: rgba(128, 137, 163, 0.24);
  }

  &.is-active {
    font-weight: 700;
    color: main.$color-primary_01;
    background-color: rgba(128, 137, 163, 0.3);
    border-color: rgba(128, 137, 163, 0.4);
  }
}

.props-filter__caret {
  font-size: 10px;
  transition: transform 0.15s ease;

  &.open {
    transform: rotate(180deg);
  }
}

.props-filter__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 158px;
  padding: 5px;
  background: main.$white_01;
  border: 1px solid main.$blue-901_01;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(30, 41, 99, 0.18);
}

.props-filter__item {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: main.$color-primary_01;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  padding: 8px 10px;
  text-align: left;
  touch-action: manipulation;
  width: 100%;
  transition:
    background-color 0.12s ease,
    color 0.12s ease;

  &:hover {
    background-color: rgba(99, 102, 241, 0.12);
    color: main.$color-primary;
  }

  &.is-active {
    color: main.$color-primary;
    background-color: rgba(99, 102, 241, 0.1);
  }
}

.props-filter__item--clear {
  color: main.$grey-2700;
  font-weight: 600;

  &:hover {
    color: #dc2626;
    background-color: rgba(220, 38, 38, 0.1);
  }
}

.props-filter__divider {
  height: 1px;
  margin: 3px 6px;
  background: main.$blue-901_01;
}

.props-filter__item-icon {
  font-size: 12px;
  width: 14px;
  opacity: 0.85;
}

.props-filter__item-label {
  flex: 1;
  white-space: nowrap;
}

.props-filter__check {
  font-size: 11px;
  color: main.$color-primary;
}

[theme='night'] .props-filter__trigger {
  color: rgba(215, 219, 255, 0.85);
  background-color: rgba(198, 205, 235, 0.1);

  &:hover {
    background-color: rgba(198, 205, 235, 0.18);
    color: main.$white;
  }

  &.is-active {
    color: main.$white;
    background-color: rgba(198, 205, 235, 0.22);
    border-color: rgba(198, 205, 235, 0.28);
  }
}

[theme='night'] .props-filter__menu {
  background: #131736;
  border-color: rgba(134, 144, 219, 0.25);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

[theme='night'] .props-filter__item {
  color: rgba(215, 219, 255, 0.85);

  &:hover {
    background-color: rgba(148, 158, 255, 0.18);
    color: main.$white;
  }

  &.is-active {
    color: main.$white;
    background-color: rgba(148, 158, 255, 0.14);
  }
}

[theme='night'] .props-filter__item--clear {
  color: rgba(215, 219, 255, 0.6);

  &:hover {
    color: #fca5a5;
    background-color: rgba(220, 38, 38, 0.18);
  }
}

[theme='night'] .props-filter__divider {
  background: rgba(134, 144, 219, 0.25);
}

@media (max-width: 768px) {
  .props-filter__trigger {
    font-size: 11px;
    padding: 4px 12px;
  }

  .props-filter__item {
    min-height: 40px;
  }
}

.hero-preset-divider {
  width: 1px;
  height: 18px;
  margin: 0 2px;
  background: main.$blue-901_01;
  flex-shrink: 0;
}

[theme='night'] .hero-preset-divider {
  background: rgba(134, 144, 219, 0.25);
}

/* Compact pills that ride inside the search bar on the right. Smaller and
   tighter than the old standalone preset row so the bar doesn't feel
   crowded. */
.hero-preset {
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 999px;
  color: main.$color-primary_01;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.85;
  padding: 4px 10px;
  // Kills any mobile tap latency / double-tap-zoom so the pill responds on the
  // first tap.
  touch-action: manipulation;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 1;
    background-color: rgba(99, 102, 241, 0.12);
  }

  &.active {
    opacity: 1;
    font-weight: 700;
    color: main.$white;
    background-color: main.$color-primary;
    border-color: main.$color-primary;
  }
}

[theme='night'] .hero-preset {
  color: rgba(215, 219, 255, 0.85);

  &:hover {
    background-color: rgba(148, 158, 255, 0.18);
    color: main.$white;
  }

  &.active {
    color: main.$white;
    background-color: main.$color-primary;
    border-color: main.$color-primary;
  }
}

.game-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

/* Cursor change is enough — no underline on hover. */
.game-link:hover .section-title {
  text-decoration: none;
}

/* Sport badge: a small icon-only chip in the top-left corner of each row.
   Absolutely positioned so the GameProps row stays vertically centered. */
.sport-badge {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.14);
  color: main.$color-primary;

  &__icon {
    font-size: 12px;
  }
}

[theme='night'] .sport-badge {
  background: rgba(148, 158, 255, 0.18);
  color: rgba(215, 219, 255, 0.92);
}

/* On phones the row is already tight; the icon-only sport badge eats space
   to the left of the player name and adds visual noise. Drop it below the
   md breakpoint — the user already filtered to a sport via the chips, or
   they're on Popular and the matchup line itself is enough context. */
@media (max-width: 768px) {
  .sport-badge {
    display: none;
  }
}

.player-view {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Player/Game group container: one card per group with internal hairline
   dividers between rows. Mirrors PRO-150 SingleGame's `.player-section`. */
.player-section {
  background: main.$white_01;
  border: 1px solid main.$blue-901_01;
  border-radius: 10px;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  margin-bottom: 0;
  overflow: hidden;
}

[theme='night'] .player-section {
  background: rgba(16, 20, 48, 0.6);
  border-color: rgba(134, 144, 219, 0.2);
}

/* Default `.props-list` (used by Popular / Category views): grid of cards.
   Tighter gap in light mode to cut down the white-space between rows that
   otherwise made the page feel sparse. */
.props-list {
  display: grid;
  gap: 8px;
}

@media (max-width: 768px) {
  .props-list {
    gap: 6px;
  }
}

.all-props-row {
  background: main.$white_01;
  border-radius: 10px;
  box-shadow: main.$box-shadow-02;
  overflow: hidden;
  /* Anchor for the sport-badge corner flag so it overlays the card instead of
     pushing the row content down (which would leave the row centered only in
     the lower portion of the card). */
  position: relative;
}

/* GameProps renders its row as `<div class="row game">`. Bootstrap's `.row`
   applies negative horizontal margins that overshoot our card, which combined
   with `overflow: hidden` makes the inner columns visually misalign. Neutralise
   so col-* children sit flush inside the card, matching SingleGame conventions
   (see `.prop-rows :deep(.row.game)` there). The padding override tightens the
   default `py-3` (16px) so cards aren't so chunky relative to the gap between
   them. */
.all-props-row :deep(.row.game) {
  margin-left: 0;
  margin-right: 0;
  padding-top: 6px;
  padding-bottom: 6px;
}

@media (max-width: 768px) {
  .all-props-row :deep(.row.game) {
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

/* Inside a `.player-section`, the props-list collapses into the card. */
.player-section .props-list {
  display: block;
  gap: 0;
  padding: 0 8px 4px;
}

.player-section-header {
  align-items: center;
  background: transparent;
  border: none;
  color: main.$color-primary_01;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  .section-title {
    flex: 1;
  }

  /* Cursor (set on .player-section-header) is enough; no hover underline. */
  &:hover .section-title {
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid main.$color-primary;
    outline-offset: 2px;
    border-radius: 4px;
  }

  .market-count {
    color: main.$grey-2700;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }
}

.disclosure-chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 20px;
  line-height: 1;
  color: main.$color-primary_01;
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &.open {
    transform: rotate(90deg);
  }
}

.section-title {
  color: main.$color-primary_01;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

@media (max-width: 768px) {
  .section-title {
    font-size: 14px;
  }

  .player-section-header {
    padding: 8px 10px;
    gap: 8px;

    .market-count {
      font-size: 10px;
    }
  }
}

/* Strip per-row card treatment from GameProps inside a section so the
   container card is the only visual unit; rows are separated by hairlines
   instead of floating shadows. Mirrors SingleGame's `.player-markets` rules. */
.player-section .props-list :deep(.game) {
  color: main.$color-primary_01;
  background: transparent;
  box-shadow: none;
  border-top: 1px solid main.$blue-901_01;
  border-radius: 0;
  margin-bottom: 0;
}

.player-section .props-list :deep(.game:first-child) {
  border-top: none;
}

/* Pull the first row up to roughly halve the visible gap to the section
   header. Negative margin only — the row's own padding, text, and +/+ bet
   blocks keep their exact size. Applies to all expanded dropdown sections,
   regardless of market count. */
.player-section .props-list :deep(.row.game:first-child) {
  margin-top: -14px;
}

[theme='night'] .player-section .props-list :deep(.game) {
  border-top-color: rgba(134, 144, 219, 0.18);
}

/* GameProps renders `<div class="row game">`; Bootstrap's `.row` adds
   negative horizontal margins that overshoot our container. Neutralise. */
.player-section .props-list :deep(.row.game) {
  margin-left: 0;
  margin-right: 0;
}

.empty-state,
.partial-error {
  background: main.$white_01;
  border-radius: 8px;
  color: main.$color-primary_01;
  padding: 16px;
  text-align: center;
}

.load-more-wrap {
  display: flex;
  justify-content: center;
}

.load-more-btn {
  background-color: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.35);
  border-radius: 999px;
  color: main.$color-primary;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 8px 22px;
  text-transform: uppercase;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: main.$color-primary;
    border-color: main.$color-primary;
    color: main.$white;
  }
}

[theme='night'] .load-more-btn {
  background-color: rgba(148, 158, 255, 0.12);
  border-color: rgba(148, 158, 255, 0.4);
  color: rgba(215, 219, 255, 0.92);

  &:hover {
    background-color: main.$color-primary;
    border-color: main.$color-primary;
    color: main.$white;
  }
}
</style>
